import React, { useState, useRef, useEffect } from 'react';
import { Button, Typography, Space, message, Card } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, LockOutlined } from '@ant-design/icons';
import { useTheme } from '../../hooks/useTheme';
import './PatternLock.scss';

const { Title, Text } = Typography;

const PatternLock = ({ 
	onPatternComplete, 
	isSetupMode = false, 
	onCancel,
	title,
	subtitle,
	isModal = false // Add prop to detect if in modal
}) => {
	const { themeColor, theme } = useTheme();
	const [currentPattern, setCurrentPattern] = useState([]);
	const [isDrawing, setIsDrawing] = useState(false);
	const [setupStep, setSetupStep] = useState(1); // 1: first pattern, 2: confirm pattern
	const [firstPattern, setFirstPattern] = useState([]);
	const [animationState, setAnimationState] = useState(''); // 'success', 'error', ''
	const canvasRef = useRef(null);
	const containerRef = useRef(null);

	const GRID_SIZE = 3;
	const DOT_RADIUS = 20;
	const LINE_WIDTH = 8;

	// Pattern grid positions (3x3)
	const getGridPositions = () => {
		if (!containerRef.current) return [];
		
		const container = containerRef.current;
		const rect = container.getBoundingClientRect();
		const size = Math.min(rect.width, rect.height) - 100;
		const spacing = size / (GRID_SIZE + 1);
		const startX = (rect.width - size) / 2;
		const startY = (rect.height - size) / 2;

		const positions = [];
		for (let row = 0; row < GRID_SIZE; row++) {
			for (let col = 0; col < GRID_SIZE; col++) {
				positions.push({
					x: startX + (col + 1) * spacing,
					y: startY + (row + 1) * spacing,
					id: row * GRID_SIZE + col
				});
			}
		}
		return positions;
	};

	// Draw pattern on canvas
	const drawPattern = () => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext('2d');
		const positions = getGridPositions();
		
		if (positions.length === 0) return;

		// Clear canvas
		ctx.clearRect(0, 0, canvas.width, canvas.height);

		// Theme-aware colors
		const primaryColor = themeColor || '#1890ff';
		const isSuccess = animationState === 'success';
		const isError = animationState === 'error';
		const dotColor = isError ? '#ff4d4f' : (isSuccess ? '#52c41a' : primaryColor);
		const lineColor = dotColor;

		// Draw dots
		positions.forEach((pos, index) => {
			const isSelected = currentPattern.includes(index);
			
			// Outer circle (border) - enhanced with glow effect
			ctx.shadowColor = isSelected ? dotColor : 'transparent';
			ctx.shadowBlur = isSelected ? 20 : 0;
			ctx.beginPath();
			ctx.arc(pos.x, pos.y, DOT_RADIUS + 4, 0, 2 * Math.PI);
			ctx.fillStyle = isSelected ? dotColor : (theme === 'dark' ? '#434343' : '#d9d9d9');
			ctx.fill();

			// Middle circle
			ctx.shadowBlur = 0;
			ctx.beginPath();
			ctx.arc(pos.x, pos.y, DOT_RADIUS, 0, 2 * Math.PI);
			ctx.fillStyle = isSelected ? '#ffffff' : (theme === 'dark' ? '#1f1f1f' : '#f0f0f0');
			ctx.fill();

			// Inner dot for selected nodes with enhanced styling
			if (isSelected) {
				ctx.shadowColor = dotColor;
				ctx.shadowBlur = 10;
				ctx.beginPath();
				ctx.arc(pos.x, pos.y, 8, 0, 2 * Math.PI);
				ctx.fillStyle = dotColor;
				ctx.fill();
				ctx.shadowBlur = 0;
			}
		});

		// Draw lines connecting selected dots with enhanced styling
		if (currentPattern.length > 1) {
			ctx.shadowColor = lineColor;
			ctx.shadowBlur = 15;
			ctx.strokeStyle = lineColor;
			ctx.lineWidth = LINE_WIDTH;
			ctx.lineCap = 'round';
			ctx.lineJoin = 'round';

			// Main line
			ctx.beginPath();
			const firstPos = positions[currentPattern[0]];
			ctx.moveTo(firstPos.x, firstPos.y);

			for (let i = 1; i < currentPattern.length; i++) {
				const pos = positions[currentPattern[i]];
				ctx.lineTo(pos.x, pos.y);
			}
			ctx.stroke();

			// Subtle overlay for glow effect
			ctx.shadowBlur = 5;
			ctx.lineWidth = LINE_WIDTH + 2;
			ctx.globalAlpha = 0.3;
			ctx.stroke();
			ctx.globalAlpha = 1;
			ctx.shadowBlur = 0;
		}
	};

	// Get point from coordinates
	const getPointFromCoordinates = (x, y) => {
		const positions = getGridPositions();
		const rect = containerRef.current?.getBoundingClientRect();
		
		if (!rect) return -1;

		const relativeX = x - rect.left;
		const relativeY = y - rect.top;

		for (let i = 0; i < positions.length; i++) {
			const pos = positions[i];
			const distance = Math.sqrt(
				Math.pow(relativeX - pos.x, 2) + Math.pow(relativeY - pos.y, 2)
			);
			if (distance <= DOT_RADIUS + 10) {
				return i;
			}
		}
		return -1;
	};

	// Handle mouse/touch events
	const handleStart = (e) => {
		e.preventDefault();
		setIsDrawing(true);
		setCurrentPattern([]);
		
		const clientX = e.touches ? e.touches[0].clientX : e.clientX;
		const clientY = e.touches ? e.touches[0].clientY : e.clientY;
		const point = getPointFromCoordinates(clientX, clientY);
		
		if (point !== -1) {
			setCurrentPattern([point]);
		}
	};

	const handleMove = (e) => {
		if (!isDrawing) return;
		e.preventDefault();
		
		const clientX = e.touches ? e.touches[0].clientX : e.clientX;
		const clientY = e.touches ? e.touches[0].clientY : e.clientY;
		const point = getPointFromCoordinates(clientX, clientY);
		
		if (point !== -1 && !currentPattern.includes(point)) {
			setCurrentPattern(prev => [...prev, point]);
		}
	};

	const handleEnd = (e) => {
		e.preventDefault();
		setIsDrawing(false);
		
		if (currentPattern.length >= 4) {
			if (isSetupMode) {
				handleSetupPattern();
			} else {
				onPatternComplete(currentPattern);
			}
		} else {
			message.warning('Pattern must connect at least 4 dots');
			setAnimationState('error');
			setCurrentPattern([]);
			setTimeout(() => setAnimationState(''), 500);
		}
	};

	// Handle setup mode (two-step pattern creation)
	const handleSetupPattern = () => {
		if (setupStep === 1) {
			setFirstPattern(currentPattern);
			setSetupStep(2);
			setAnimationState('success');
			setTimeout(() => {
				setCurrentPattern([]);
				setAnimationState('');
			}, 600);
		} else {
			// Confirm pattern
			if (JSON.stringify(currentPattern) === JSON.stringify(firstPattern)) {
				setAnimationState('success');
				setTimeout(() => {
					onPatternComplete(currentPattern);
					message.success('Pattern set successfully!');
				}, 600);
			} else {
				setAnimationState('error');
				message.error('Patterns do not match. Please try again.');
				setTimeout(() => {
					setSetupStep(1);
					setFirstPattern([]);
					setCurrentPattern([]);
					setAnimationState('');
				}, 800);
			}
		}
	};

	const resetPattern = () => {
		setCurrentPattern([]);
		if (isSetupMode) {
			setSetupStep(1);
			setFirstPattern([]);
		}
	};

	// Resize canvas and redraw when component mounts or resizes
	useEffect(() => {
		const resizeCanvas = () => {
			const canvas = canvasRef.current;
			const container = containerRef.current;
			
			if (!canvas || !container) return;

			const rect = container.getBoundingClientRect();
			canvas.width = rect.width;
			canvas.height = rect.height;
			drawPattern();
		};

		resizeCanvas();
		window.addEventListener('resize', resizeCanvas);
		
		return () => window.removeEventListener('resize', resizeCanvas);
	}, []);

	// Redraw when pattern changes
	useEffect(() => {
		drawPattern();
	}, [currentPattern]);

	return (
		<div className={`pattern-lock ${isModal ? 'pattern-lock-modal' : ''} ${(title || subtitle) ? 'no-header' : ''} ${animationState ? `pattern-${animationState}` : ''}`}>
			{/* Only show header if title/subtitle not provided externally */}
			{(!title && !subtitle) && (
				<div className="pattern-lock-header">
					<Space direction="vertical" size="small" style={{ textAlign: 'center', width: '100%' }}>
						<div className="pattern-icon">
							<LockOutlined style={{ 
								fontSize: '48px', 
								color: isModal ? themeColor : 'white',
								filter: `drop-shadow(0 2px 8px ${themeColor}40)`
							}} />
						</div>
						<Title level={3} style={{ 
							margin: 0, 
							color: isModal ? undefined : 'white',
							textShadow: isModal ? 'none' : '0 2px 4px rgba(0,0,0,0.3)'
						}}>
							{isSetupMode ? 'Set Lock Pattern' : 'Enter Lock Pattern'}
						</Title>
						<Text type="secondary" style={{ 
							color: isModal ? undefined : 'rgba(255,255,255,0.8)',
							fontSize: '16px'
						}}>
							{isSetupMode 
								? (setupStep === 1 ? 'Draw your pattern (connect at least 4 dots)' : 'Draw the pattern again to confirm')
								: 'Draw your pattern to unlock'
							}
						</Text>
					</Space>
				</div>
			)}

			{/* Connection Indicator - shown for all modes when pattern is being drawn */}
			{/* {currentPattern.length > 0 && (
				<div className="pattern-status">
					<Space direction="vertical" size="small" style={{ width: '100%', alignItems: 'center' }}>
						<div className="connection-indicator">
							{Array.from({ length: 9 }, (_, i) => (
								<div 
									key={i} 
									className={`dot ${i < currentPattern.length ? 'active' : ''}`}
									style={{ backgroundColor: i < currentPattern.length ? themeColor : undefined }}
								/>
							))}
						</div>
						<Text strong className="connection-text">
							{currentPattern.length} of 9 dots
						</Text>
						<div className="pattern-progress-bar">
							<div 
								className="pattern-progress-fill"
								style={{ 
									width: `${(currentPattern.length / 9) * 100}%`,
									backgroundColor: themeColor 
								}}
							/>
						</div>
					</Space>
				</div>
			)} */}

			<div 
				ref={containerRef}
				className="pattern-container"
				onMouseDown={handleStart}
				onMouseMove={handleMove}
				onMouseUp={handleEnd}
				onMouseLeave={handleEnd}
				onTouchStart={handleStart}
				onTouchMove={handleMove}
				onTouchEnd={handleEnd}
			>
				<canvas 
					ref={canvasRef}
					className="pattern-canvas"
				/>
				<div className="pattern-overlay">
					<div className="grid-hint">
						<Text type="secondary" style={{ fontSize: '12px', opacity: 0.6 }}>
							3×3 Grid Pattern
						</Text>
					</div>
				</div>
			</div>

			<div className="pattern-lock-actions">
				<Space size="large">
					{onCancel && (
						<Button 
							icon={<ArrowLeftOutlined />} 
							onClick={onCancel}
							size="large"
							className={`action-button ${isModal ? 'modal-button' : ''}`}
							type={isModal ? "default" : "primary"}
						>
							Back
						</Button>
					)}
					<Button 
						icon={<ReloadOutlined />} 
						onClick={resetPattern}
						size="large"
						className={`action-button ${isModal ? 'modal-button' : ''}`}
						type={isModal ? "default" : "primary"}
					>
						Clear
					</Button>
				</Space>
			</div>

			{/* Setup Progress Indicator */}
			{isSetupMode && (
				<div className="setup-progress">
					<div className={`progress-step ${setupStep >= 1 ? 'active' : ''}`}>
						<div className="step-number">1</div>
						<Text>Create</Text>
					</div>
					<div className="progress-line" />
					<div className={`progress-step ${setupStep >= 2 ? 'active' : ''}`}>
						<div className="step-number">2</div>
						<Text>Confirm</Text>
					</div>
				</div>
			)}
		</div>
	);
};

export default PatternLock;